import { Form, Col, Row, Button } from "react-bootstrap";
import { useRef } from "react";
import i18next from 'i18next';
import RequiredFormLabel from '../../RequiredFormLabel/RequiredFormLabel';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { useApiClient } from '../../../hooks/useApiClient';
import toaster from '../../../utils/toaster'
import useLeadsStore from '../../../store/LeadsStore';
import { useAuth } from '../../../hooks/useAuth';
import { useMemo, useState } from "react";
import _ from 'lodash';
import '../../../pages/Leads/LeadsPage.scss';
import { LeadReminderStatusEnum } from "../../../api/models/Enums";
import { BiTrash } from "react-icons/bi";
import { AiOutlineClose } from "react-icons/ai";
import { useErrorToast } from '../../../hooks/useErrorToast';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { isMarket } from "src/utils/markets";
import { Markets } from "src/types";

const LeadsReminders = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation()
  const { apiClient, handleApiResponse } = useApiClient();
  const { activeLead } = useLeadsStore()
  const { user } = useAuth();
  const [editingReminderId, setEditingReminderId] = useState<string | null>(null);
  const [editedValues, setEditedValues] = useState<{ event_content: string; scheduled_at: string } | null>(null);
  const currentRef = useRef<{ resetForm?: () => void }>({})
  const { showError } = useErrorToast();

  const initialValues = {
    event_content: "",
    scheduled_at: ""
  };

  const tz = useMemo(() => {
    return isMarket(Markets.es) ? 'Europe/Madrid' : 'Europe/Rome';
  }, [])

  const formatDateInTimezone = (utcString: string) => {
    if (!utcString) return '';
    return formatInTimeZone(new Date(utcString + 'Z'), tz, 'dd/MM/yyyy HH:mm');
  };

  const utcToLocalDateTimeString = (utcString: string) => {
    if (!utcString) return '';
    const date = new Date(utcString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const hasChanges = (
    original: { event_content: string; scheduled_at: string },
    current: { event_content: string; scheduled_at: string }
  ) => {
    if (original.event_content !== current.event_content) {
      return true;
    }

    const originalDate = new Date(original.scheduled_at);
    const currentDate = new Date(current.scheduled_at);
    return originalDate.getTime() !== currentDate.getTime();
  };

  const validationSchema = Yup.object().shape({
    event_content: Yup.string().required(i18next.t('notification_mandatory')),
    scheduled_at: Yup.string()
      .required(i18next.t('date_time_mandatory'))
      .test(
        'is-valid-date',
        i18next.t('invalid_date'),
        (value) => {
          if (!value) return false;
          return !isNaN(new Date(value).getTime());
        }
      )
      .test(
        'is-future-date',
        i18next.t('date_must_be_future'),
        (value) => {
          if (!value) return false;
          const selected = new Date(value);
          const now = new Date();
          return selected > now;
        }
      ),

  });


  const submitFn = (values, { resetForm }) => {
    saveNotification({
      event_content: values.event_content,
      scheduled_at: new Date(values.scheduled_at).toISOString(),
      recipient_email: user.username,
      lead_first_name: activeLead.FirstName,
      lead_last_name: activeLead.LastName,
      target_url: `${window.location.origin}/leads/?id=${activeLead.Id}`,
    })
    currentRef.current.resetForm = resetForm;
  };


  const handleUpdateReminder = (leadId: string, reminderId: string, eventContent: string, scheduledAt: string, item: any) => {
    if (!eventContent || !scheduledAt) {
      toaster.error(i18next.t('missing_required_fields'));
      return;
    }
    item.target_url = item.target_url || `${window.location.origin}/leads/?id=${leadId}`;
    item.lead_first_name = item.lead_first_name || activeLead.FirstName;
    item.lead_last_name = item.lead_last_name || activeLead.LastName;
    item.error_message = null;

    const payload = {
      item,
      event_content: eventContent,
      scheduled_at: new Date(scheduledAt + "Z").toISOString(),
    };

    updateReminder({ leadId, reminderId, ...payload });
  };

  const handleDeleteReminder = (leadId: string, reminderId: string) => {
    apiClient.leadsControllerService.deleteLeadReminder(leadId, reminderId)
      .then(() => {
        toaster.success(i18next.t('lead_reminder_deleted_successfully'));
        queryClient.invalidateQueries({ queryKey: ["lead_reminders"] });
      })
      .catch((error) => {
        console.error("Error deleting lead reminder:", error);
        showError(error, i18next.t('error_deleting_lead_reminder'));
      });
  };

  const { mutate: updateReminder } = useMutation({
    mutationFn: ({ leadId, reminderId, event_content, scheduled_at, item }: {
      leadId: string;
      reminderId: string;
      event_content: string;
      scheduled_at: string;
      item: any
    }) => apiClient.leadsControllerService.updateLeadReminder(leadId, reminderId, {
      ...(item || {}),
      status: 'PENDING',
      event_content,
      scheduled_at,
      id: reminderId,
      recipient_email: user.username,
    }),
    onSuccess: () => {
      toaster.success(i18next.t('lead_reminder_updated_successfully'));
      queryClient.invalidateQueries({ queryKey: ["lead_reminders"] });
      setEditingReminderId(null);
      setEditedValues(null);
    },
    onError: (error) => {
      showError(error, i18next.t('error_updating_lead_reminder'));
      console.error("Error updating lead reminder:", error);
    },
  });

  const { data: leadReminders = [], isPending: isLoadingReminders } = useQuery({
    queryKey: ['lead_reminders', activeLead?.Id],
    enabled: !!activeLead?.Id,
    queryFn: async () => {
      let data: any;
      try {
        const res = await handleApiResponse(apiClient.leadsControllerService.getLeadReminders(activeLead?.Id));
        data = res;
      } catch (error) {
        data = [];
        console.error("Error fetching lead reminders:", error);
      }

      return (data || []).filter((reminder: any) => reminder.status !== LeadReminderStatusEnum.DELETED);
    },
    retry: false
  });

  const { mutate: saveNotification, isPending: isSaving } = useMutation({
    mutationFn: (payload: any) => apiClient.leadsControllerService.createLeadReminder(activeLead.Id, payload),
    onSuccess: (data) => {
      toaster.success(i18next.t('lead_reminder_created_succesfully'));
      queryClient.invalidateQueries({ queryKey: ["lead_reminders"] });
      currentRef.current.resetForm?.();
    },
    onError: (error) => {
      console.error("Error creating lead reminder:", error);
      showError(error, i18next.t('error_creating_lead_reminder'));
    },
  });



  return (
    <div className="mb-5 mt-3">
      <Row>
        <Col md={6} className="display-6 mb-3">
          {t('lead_reminders')}
        </Col>
      </Row>

      <Formik
        initialValues={initialValues}
        onSubmit={submitFn}
        validationSchema={validationSchema}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <Form onSubmit={handleSubmit}>
            <Row className="reminders-container align-items-stretch">
              <Col style={{ paddingRight: "0px" }}>
                <div className="d-flex flex-column">
                  <Form.Group>
                    <RequiredFormLabel
                      name="event_content"
                      required={true}
                      label={t('reminder')}
                    />
                    <Form.Control
                      id="reminderAnchor"
                      as="textarea"
                      rows={1}
                      name="event_content"
                      onChange={handleChange}
                      value={values.event_content ?? ''}
                      isInvalid={!!errors.event_content && touched.event_content}
                    />
                  </Form.Group>
                  {touched.event_content && errors.event_content ? (
                    <div style={{ color: 'red', minHeight: '20px' }}>{errors.event_content}</div>
                  ) : (
                    <div style={{ minHeight: '20px' }}></div>
                  )}
                </div>
              </Col>

              <Col style={{ paddingLeft: "0px" }}>
                <div className="d-flex flex-column">
                  <Form.Group>
                    <RequiredFormLabel
                      name="scheduled_at"
                      required={true}
                      label={t("date_time")}
                    />
                    <Form.Control
                      type="datetime-local"
                      name="scheduled_at"
                      value={utcToLocalDateTimeString(values.scheduled_at)}
                      onChange={(e) => setFieldValue("scheduled_at", e.target.value)}
                      onBlur={handleBlur}
                      isInvalid={touched.scheduled_at && !!errors.scheduled_at}
                    />
                  </Form.Group>
                  {touched.scheduled_at && errors.scheduled_at ? (
                    <div style={{ color: 'red', minHeight: '20px' }}>{errors.scheduled_at}</div>
                  ) : (
                    <div style={{ minHeight: '20px' }}></div>
                  )}
                </div>
              </Col>
              <Col style={{ padding: "0px", marginTop: "8px", marginLeft: "-10px", alignSelf: "center" }}>
                <Button variant="primary" type="submit" disabled={isSaving}>
                  {t("add")}
                </Button>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>

      {!_.isEmpty(leadReminders) ? (
        <div
          style={{
            maxHeight: leadReminders.length > 15 ? '700px' : 'auto',
            overflowY: leadReminders.length > 15 ? 'auto' : 'visible',
            overflowX: 'hidden',
          }}
        >
          {leadReminders.map((item) => {
            const isEditing = editingReminderId === item.id;
            const currentValues = editedValues || { ...item, event_content: item.event_content, scheduled_at: item.scheduled_at };
            return (
              <div
                className="reminders-container"
                style={{ minHeight: "50px" }}
                key={item.id || `${item.event_content}-${item.scheduled_at}`}
              >
                {isEditing ? (
                  <>
                    <Form.Control
                      as="textarea"
                      rows={1}
                      value={currentValues.event_content}
                      onChange={(e) => setEditedValues({ ...currentValues, event_content: e.target.value })}
                      isInvalid={false}
                    />
                    <Form.Control
                      type="datetime-local"
                      value={utcToLocalDateTimeString(currentValues.scheduled_at + "Z")}
                      onChange={(e) => {
                        setEditedValues({
                          ...currentValues,
                          scheduled_at: fromZonedTime(e.target.value, tz).toISOString().slice(0, 16)
                        })
                      }}
                      isInvalid={false}
                    />
                    <Button
                      className=""
                      onClick={() => {
                        if (currentValues.event_content && currentValues.scheduled_at) {
                          handleUpdateReminder(activeLead.Id, item.id, currentValues.event_content, currentValues.scheduled_at, item);
                        }
                      }}
                      disabled={!hasChanges({ event_content: item.event_content, scheduled_at: item.scheduled_at }, currentValues)}
                    >
                      {t("save")}
                    </Button>
                    <div>
                      <AiOutlineClose
                        onClick={() => setEditingReminderId(null)}
                        className="fs-4 ms-2 cursor-pointer"
                        style={{ cursor: 'pointer' }}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <Col>{item.event_content}</Col>
                    <Col style={{ paddingLeft: "10px" }}>{formatDateInTimezone(item.scheduled_at)}</Col>
                    <Col>
                      {item.status !== LeadReminderStatusEnum.SENT && (
                        <Button
                          className="w-100"
                          onClick={() => {
                            setEditingReminderId(item.id);
                            setEditedValues({ event_content: item.event_content, scheduled_at: item.scheduled_at });
                          }}
                          style={{ padding: "6px 0px" }}
                        >
                          {t("edit")}
                        </Button>
                      )}
                    </Col>
                    <Col>
                      <BiTrash
                        onClick={() => handleDeleteReminder(activeLead.Id, item.id)}
                        className="text-danger fs-4 ms-2 cursor-pointer"
                        style={{ cursor: 'pointer' }}
                      />
                    </Col>
                  </>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="d-flex justify-content-center text-muted mt-3">
          {t('no_reminders')}
        </div>
      )}
    </div>
  );
}

export default LeadsReminders
